<template>
	<aside
		class="w-[240px] min-h-screen overflow-y-auto flex-shrink-0 flex flex-col px-4 py-6 bg-white dark:bg-dark-700 border-r border-primary-100 dark:border-dark-600 transition-colors duration-300">
		<!-- ── Logo ── -->
		<div class="flex items-center gap-2.5 px-2 mb-8">
			<div
				class="w-8 h-8 rounded-xl bg-primary-400 flex items-center justify-center flex-shrink-0 shadow-teal">
				<svg
					viewBox="0 0 24 24"
					fill="none"
					stroke="white"
					stroke-width="2.5"
					stroke-linecap="round"
					stroke-linejoin="round"
					class="w-4 h-4">
					<path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
					<polyline points="9 22 9 12 15 12 15 22" />
				</svg>
			</div>
			<span
				class="font-display text-[17px] font-extrabold tracking-tight text-dark-700 dark:text-white">
				Trekko
			</span>
			<span
				class="ml-auto w-2 h-2 rounded-full bg-primary-400 animate-blink flex-shrink-0" />
		</div>

		<!-- ── MENU label ── -->
		<p
			class="text-[10px] font-bold uppercase tracking-[0.14em] px-3 mb-2 text-muted dark:text-white/30">
			Menu
		</p>

		<nav class="flex flex-col gap-0.5 mb-6">
			<router-link
				to="/admin/dashboard"
				class="nav-link"
				active-class="nav-link--active">
				<span class="nav-icon"
					><i class="pi pi-objects-column text-[12px]"
				/></span>
				<span>Dashboard</span>
				<span
					v-if="getBadge('dashboard')"
					class="ml-auto text-[10px] font-bold rounded-full px-2 py-0.5 flex-shrink-0 bg-primary-100 text-primary-600 dark:bg-primary-400/20 dark:text-primary-300">
					{{ getBadge("dashboard").label }}
				</span>
			</router-link>

			<router-link
				v-if="forAdmin"
				to="/admin/category"
				class="nav-link"
				active-class="nav-link--active">
				<span class="nav-icon"><i class="pi pi-tags text-[12px]" /></span>
				<span>Category</span>
			</router-link>

			<router-link
				v-if="forAdmin || forClient"
				to="/admin/event/all"
				class="nav-link"
				active-class="nav-link--active">
				<span class="nav-icon"><i class="pi pi-calendar text-[12px]" /></span>
				<span>Event Management</span>
				<span
					v-if="getBadge('events')"
					class="ml-auto text-[10px] font-bold rounded-full px-2 py-0.5 flex-shrink-0 bg-primary-100 text-primary-600 dark:bg-primary-400/20 dark:text-primary-300">
					{{ getBadge("events").label }}
				</span>
			</router-link>

			<router-link
				v-if="forAdmin || forClient"
				to="/admin/user/all"
				class="nav-link"
				active-class="nav-link--active">
				<span class="nav-icon"><i class="pi pi-users text-[12px]" /></span>
				<span>Accounts</span>
			</router-link>

			<router-link
				v-if="forAdmin"
				to="/admin/organization/all"
				class="nav-link"
				active-class="nav-link--active">
				<span class="nav-icon"><i class="pi pi-briefcase text-[12px]" /></span>
				<span>Client</span>
			</router-link>

			<router-link
				v-if="forAdmin || forClient"
				to="/admin/marketing"
				class="nav-link"
				active-class="nav-link--active">
				<span class="nav-icon"
					><i class="pi pi-chart-scatter text-[12px]"
				/></span>
				<span>Growth & Outreach</span>
				<span
					v-if="getBadge('marketing')"
					class="ml-auto text-[10px] font-bold rounded-full px-2 py-0.5 flex-shrink-0 bg-primary-100 text-primary-600 dark:bg-primary-400/20 dark:text-primary-300">
					{{ getBadge("marketing").label }}
				</span>
			</router-link>

			<router-link
				v-if="forAdmin || forClient"
				to="/admin/revenue"
				class="nav-link"
				active-class="nav-link--active">
				<span class="nav-icon"><i class="pi pi-wallet text-[12px]" /></span>
				<span>Revenue</span>
				<span
					v-if="getBadge('revenue')"
					class="ml-auto text-[10px] font-bold rounded-full px-2 py-0.5 flex-shrink-0 bg-primary-100 text-primary-600 dark:bg-primary-400/20 dark:text-primary-300">
					{{ getBadge("revenue").label }}
				</span>
			</router-link>
		</nav>

		<!-- ── GENERAL label ── -->
		<p
			class="text-[10px] font-bold uppercase tracking-[0.14em] px-3 mb-2 text-muted dark:text-white/30">
			General
		</p>

		<nav class="flex flex-col gap-0.5">
			<router-link
				v-if="forAdmin"
				to="/admin/security-access-control"
				class="nav-link"
				active-class="nav-link--active">
				<span class="nav-icon"><i class="pi pi-shield text-[12px]" /></span>
				<span>Security & Access</span>
			</router-link>

			<router-link
				v-if="forAdmin"
				to="/admin/settings"
				class="nav-link"
				active-class="nav-link--active">
				<span class="nav-icon"><i class="pi pi-cog text-[12px]" /></span>
				<span>Settings</span>
			</router-link>

			<!-- Dark mode toggle -->
			<button class="nav-link w-full text-left" @click="toggle">
				<span class="nav-icon">
					<i :class="isDark ? 'pi pi-sun' : 'pi pi-moon'" class="text-[12px]" />
				</span>
				<span>{{ isDark ? "Light Mode" : "Dark Mode" }}</span>
				<span class="ml-auto flex-shrink-0">
					<span
						class="relative inline-flex items-center w-9 h-5 rounded-full transition-colors duration-300"
						:class="isDark ? 'bg-primary-400' : 'bg-dark-200'">
						<span
							class="absolute w-3.5 h-3.5 rounded-full bg-white shadow transition-transform duration-300"
							:class="isDark ? 'translate-x-[18px]' : 'translate-x-[3px]'" />
					</span>
				</span>
			</button>

			<button class="nav-link w-full text-left">
				<span class="nav-icon"
					><i class="pi pi-question-circle text-[12px]"
				/></span>
				<span>Help</span>
			</button>
		</nav>

		<!-- ── Promo card ── -->
		<div class="sticky bottom-0 pt-6 pb-2 -mx-4 px-4 bg-white dark:bg-dark-700">
			<div
				class="relative rounded-2xl p-4 overflow-hidden bg-primary-50 border border-primary-100 dark:bg-primary-400/10 dark:border-primary-400/20">
				<div
					class="absolute w-24 h-24 rounded-full -top-8 -right-6 pointer-events-none bg-primary-200 dark:bg-primary-400/15" />
				<div
					class="absolute w-12 h-12 rounded-full bottom-2 left-2 pointer-events-none bg-accent-200 dark:bg-accent-400/10" />
				<p
					class="font-display text-[12px] font-extrabold mb-1 relative z-10 text-dark-700 dark:text-white">
					Download our App
				</p>
				<p
					class="text-[10px] leading-relaxed mb-3 relative z-10 text-muted dark:text-white/45">
					Get easy in another way
				</p>
				<router-link
					to="/admin/download"
					class="relative z-10 w-full font-display text-[11px] font-bold bg-primary-400 text-white rounded-btn p-2 border-none cursor-pointer hover:bg-primary-600 transition-colors duration-200">
					Download
				</router-link>
			</div>
		</div>
	</aside>
</template>

<script>
import { useTheme } from "@/composables/useTheme";
import { useBadgeVisibility } from "@/composables/useBadgeVisibility";

export default {
	name: "SideBar",

	setup() {
		const { isDark, toggle } = useTheme();
		const { getBadge } = useBadgeVisibility();
		return { isDark, toggle, getBadge };
	},

	computed: {
		userInfo() {
			return this.$store.state.auth.user || {};
		},
		forAdmin() {
			const type = Number(this.userInfo.user_type);
			return type === 0;
		},
		forClient() {
			const type = Number(this.userInfo.user_type);
			return type !== 1 && type == 2;
		},
	},
};
</script>

<style scoped>
/* ── Light mode nav link ── */
.nav-link {
	@apply flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium
         no-underline transition-all duration-200 cursor-pointer border-none bg-transparent
         text-dark-400 dark:text-white/50
         hover:bg-primary-50 hover:text-primary-600
         dark:hover:bg-primary-400/15 dark:hover:text-white;
}

/* ── Icon box ── */
.nav-icon {
	@apply w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
         transition-colors duration-200
         bg-primary-50 text-muted
         dark:bg-white/5 dark:text-white/50;
}

.nav-link:hover .nav-icon {
	@apply bg-primary-100 text-primary-600
         dark:bg-primary-400/20 dark:text-primary-300;
}

/* ── Active state ── */
.nav-link--active {
	@apply bg-primary-400 text-white font-semibold shadow-teal
         dark:bg-primary-400 dark:text-white;
}

.nav-link--active .nav-icon {
	@apply bg-white/20 text-white
         dark:bg-white/20 dark:text-white;
}
</style>
